import React from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';

/**
 * A required Yes / No question with the same error wiring ValidatedField
 * gives a text control.
 *
 * The group is a radiogroup so a screen reader announces the legend, the
 * required state, and the error once for the pair, and `${controlId}-yes` is
 * the control a failed submit focuses. The value is 'yes', 'no', or '' for
 * unanswered, which keeps an unanswered question distinct from an answered No.
 */
function YesNoQuestion({
    controlId,
    legend,
    value,
    onChange,
    error = '',
    help = '',
}) {
    const describedBy =
        [error ? `${controlId}-feedback` : '', help ? `${controlId}-help` : '']
            .filter(Boolean)
            .join(' ') || undefined;
    return (
        <fieldset
            className="mb-3"
            role="radiogroup"
            aria-required="true"
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={describedBy}
        >
            <legend className="form-label fs-6">
                {legend}
                <span className="text-muted fw-normal"> (required)</span>
            </legend>
            {help && (
                <Form.Text id={`${controlId}-help`} as="p" className="mb-1">
                    {help}
                </Form.Text>
            )}
            <Form.Check
                inline
                type="radio"
                name={controlId}
                id={`${controlId}-yes`}
                label="Yes"
                value="yes"
                checked={value === 'yes'}
                isInvalid={Boolean(error)}
                onChange={() => onChange('yes')}
            />
            <Form.Check
                inline
                type="radio"
                name={controlId}
                id={`${controlId}-no`}
                label="No"
                value="no"
                checked={value === 'no'}
                isInvalid={Boolean(error)}
                onChange={() => onChange('no')}
            />
            {error && (
                <div
                    id={`${controlId}-feedback`}
                    className="invalid-feedback d-block"
                >
                    {error}
                </div>
            )}
        </fieldset>
    );
}

YesNoQuestion.propTypes = {
    controlId: PropTypes.string.isRequired,
    legend: PropTypes.string.isRequired,
    value: PropTypes.oneOf(['yes', 'no', '']).isRequired,
    onChange: PropTypes.func.isRequired,
    error: PropTypes.string,
    help: PropTypes.node,
};

export default YesNoQuestion;
